/**
 * Utility Functions - Vanilla JS Helpers
 *
 * Modern replacements for common jQuery operations
 *
 * @package ultamation
 * @since 3.1.0
 */

/**
 * DOM Ready utility (replaces jQuery's $(document).ready())
 * @param {Function} fn - Function to execute when DOM is ready
 */
export function ready(fn) {
  if (document.readyState !== "loading") {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

/**
 * Query selector helper (replaces jQuery's $())
 * @param {string} selector - CSS selector
 * @param {Element} context - Optional context element
 * @returns {Element|null}
 */
export function $(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * Query selector all helper (replaces jQuery's $())
 * @param {string} selector - CSS selector
 * @param {Element} context - Optional context element
 * @returns {NodeList}
 */
export function $$(selector, context = document) {
  return context.querySelectorAll(selector);
}

/**
 * Add class helper
 * @param {Element} el - Element to add class to
 * @param {string} className - Class name to add
 */
export function addClass(el, className) {
  if (el) el.classList.add(className);
}

/**
 * Remove class helper
 * @param {Element} el - Element to remove class from
 * @param {string} className - Class name to remove
 */
export function removeClass(el, className) {
  if (el) el.classList.remove(className);
}

/**
 * Toggle class helper (replaces jQuery's toggleClass())
 * @param {Element} el - Element to toggle class on
 * @param {string} className - Class name to toggle
 */
export function toggleClass(el, className) {
  if (el) el.classList.toggle(className);
}

/**
 * Has class helper
 * @param {Element} el - Element to check
 * @param {string} className - Class name to check for
 * @returns {boolean}
 */
export function hasClass(el, className) {
  return el ? el.classList.contains(className) : false;
}

/**
 * Slide down animation (replaces jQuery's slideDown())
 * @param {Element} el - Element to slide down
 * @param {number} duration - Animation duration in ms
 */
export function slideDown(el, duration = 300) {
  if (!el) return;

  el.style.removeProperty("display");
  let display = window.getComputedStyle(el).display;
  if (display === "none") display = "block";
  el.style.display = display;

  const height = el.offsetHeight;
  el.style.overflow = "hidden";
  el.style.height = 0;
  el.style.paddingTop = 0;
  el.style.paddingBottom = 0;
  el.style.marginTop = 0;
  el.style.marginBottom = 0;
  el.offsetHeight; // Force reflow
  el.style.transition = `height ${duration}ms ease, padding ${duration}ms ease, margin ${duration}ms ease`;

  el.style.height = height + "px";
  el.style.removeProperty("padding-top");
  el.style.removeProperty("padding-bottom");
  el.style.removeProperty("margin-top");
  el.style.removeProperty("margin-bottom");

  setTimeout(() => {
    el.style.removeProperty("height");
    el.style.removeProperty("overflow");
    el.style.removeProperty("transition");
  }, duration);
}

/**
 * Slide up animation (replaces jQuery's slideUp())
 * @param {Element} el - Element to slide up
 * @param {number} duration - Animation duration in ms
 */
export function slideUp(el, duration = 300) {
  if (!el) return;

  el.style.transition = `height ${duration}ms ease, padding ${duration}ms ease, margin ${duration}ms ease`;
  el.style.height = el.offsetHeight + "px";
  el.style.overflow = "hidden";
  el.offsetHeight; // Force reflow
  el.style.height = 0;
  el.style.paddingTop = 0;
  el.style.paddingBottom = 0;
  el.style.marginTop = 0;
  el.style.marginBottom = 0;

  setTimeout(() => {
    el.style.display = "none";
    el.style.removeProperty("height");
    el.style.removeProperty("padding-top");
    el.style.removeProperty("padding-bottom");
    el.style.removeProperty("margin-top");
    el.style.removeProperty("margin-bottom");
    el.style.removeProperty("overflow");
    el.style.removeProperty("transition");
  }, duration);
}

/**
 * AJAX helper (replaces jQuery.ajax())
 * @param {Object} options - AJAX options
 * @returns {Promise}
 */
export async function ajax(options) {
  const defaults = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  };

  const config = { ...defaults, ...options };

  if (config.data && typeof config.data === "object") {
    config.body = new URLSearchParams(config.data).toString();
  }

  try {
    const response = await fetch(config.url, config);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("AJAX Error:", error);
    throw error;
  }
}

/**
 * Delegate event listener (replaces jQuery's .on() with delegation)
 * @param {string} selector - Selector for delegated elements
 * @param {string} eventType - Event type (e.g., 'click')
 * @param {Function} handler - Event handler function
 * @param {Element} context - Context element (default: document)
 */
export function delegate(selector, eventType, handler, context = document) {
  context.addEventListener(eventType, (e) => {
    const target = e.target.closest(selector);
    if (target) {
      handler.call(target, e);
    }
  });
}

/**
 * Get/Set attribute helper
 * @param {Element} el - Element
 * @param {string} attr - Attribute name
 * @param {string} value - Optional value to set
 * @returns {string|undefined}
 */
export function attr(el, attr, value) {
  if (!el) return;
  if (value !== undefined) {
    el.setAttribute(attr, value);
  } else {
    return el.getAttribute(attr);
  }
}

/**
 * Focus trap utility for modals/drawers
 * @param {Element} container - Container element
 */
export function trapFocus(container) {
  const focusableElements = container.querySelectorAll(
    "a[href], button:not([disabled]), textarea, input, select",
  );

  if (focusableElements.length === 0) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  container.addEventListener("keydown", (e) => {
    if (e.key !== "Tab") return;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  });

  // Focus first element
  setTimeout(() => firstElement.focus(), 100);
}
